import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { X, Loader2, Mail, MessageSquare, CheckCircle2, Send, Trash2, Sparkles, Eye, FileText, ExternalLink, Link2Off, AlertTriangle, Paperclip } from "lucide-react";
import { toast } from "sonner";

const CHANNEL_META = {
  email: { label: "Email", icon: Mail, color: "text-sky-600" },
  sms: { label: "SMS", icon: MessageSquare, color: "text-emerald-600" },
};

export default function CoachActionDrawer({ initial, onClose, onDone }) {
  // `initial` shape when starting fresh from a priority signal:
  //   { kind, entity_type, entity_id, potential_value }
  // when opening an existing action:
  //   { action_id }
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null);
  const [channel, setChannel] = useState("email");
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [previewTpl, setPreviewTpl] = useState(null);
  const [aiFallback, setAiFallback] = useState(null); // stores AI draft when user picks a template
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [unlinking, setUnlinking] = useState(false);

  const priorityKindToActionKind = (k) => {
    if (k === "follow_up_estimate") return "follow_up_estimate";
    if (k === "follow_up_lead") return "follow_up_lead";
    if (k === "collect_overdue") return "collect_invoice";
    if (k === "reengage_customer") return "reengage_customer";
    return "follow_up_lead";
  };

  useEffect(() => {
    let stop = false;
    (async () => {
      if (initial?.action_id) {
        setLoading(true);
        try {
          const { data } = await api.get(`/business-coach/actions/${initial.action_id}`);
          if (!stop) { setAction(data); setChannel(data.channel); }
        } finally { if (!stop) setLoading(false); }
        return;
      }
      // Draft a fresh action
      setLoading(true);
      setDrafting(true);
      try {
        const kind = priorityKindToActionKind(initial.kind);
        // Fetch template suggestions in parallel with the draft call.
        const [draftRes, suggRes] = await Promise.all([
          api.post("/business-coach/actions/draft", {
            kind, channel: initial.channel || "email",
            entity_type: initial.entity_type,
            entity_id: initial.entity_id,
          }),
          api.get(`/business-coach/templates/suggest?action_type=${kind}&channel=${initial.channel || "email"}`)
             .catch(() => ({ data: { suggestions: [] } })),
        ]);
        if (!stop) {
          setAction(draftRes.data);
          setChannel(draftRes.data.channel);
          setAiFallback({ body: draftRes.data.body, subject: draftRes.data.subject });
          setSuggestions(suggRes.data?.suggestions || []);
        }
      } catch (e) {
        if (!stop) {
          const msg = e?.response?.data?.detail || "Failed to draft follow-up";
          toast.error(msg);
          onClose?.();
        }
      } finally {
        if (!stop) { setLoading(false); setDrafting(false); }
      }
    })();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isTerminal = action && ["sent", "failed", "discarded"].includes(action.status);

  // Fetch the send-time preview (signature, linked-estimate summary, PDF status)
  // whenever the action loads or its estimate link changes. This is what
  // the owner sees BEFORE clicking Approve & send.
  useEffect(() => {
    if (!action?.id || action.channel !== "email") { setPreview(null); return; }
    let stop = false;
    setPreviewLoading(true);
    api.get(`/business-coach/actions/${action.id}/preview`)
      .then(({ data }) => { if (!stop) setPreview(data); })
      .catch(() => { if (!stop) setPreview(null); })
      .finally(() => { if (!stop) setPreviewLoading(false); });
    return () => { stop = true; };
  }, [action?.id, action?.linked_estimate_id, action?.channel]);

  const unlinkEstimate = async () => {
    if (!action) return;
    if (!window.confirm(
      "Remove the estimate from this follow-up? The email will send with the tenant signature only — no estimate details, link, or attachment."
    )) return;
    setUnlinking(true);
    try {
      const { data } = await api.patch(`/business-coach/actions/${action.id}`,
        { linked_estimate_id: "" });
      setAction(data);
      toast.success("Estimate removed from this follow-up");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove estimate");
    } finally { setUnlinking(false); }
  };

  const save = async () => {
    if (!action) return;
    setSaving(true);
    try {
      const payload = {
        subject: action.subject || "",
        body: action.body || "",
        target_email: action.target_email || undefined,
        target_phone: action.target_phone || undefined,
      };
      const { data } = await api.patch(`/business-coach/actions/${action.id}`, payload);
      setAction(data);
      toast.success("Draft saved");
    } catch (e) {
      toast.error("Failed to save");
    } finally { setSaving(false); }
  };

  const approveSend = async () => {
    if (!action) return;
    if (!window.confirm(
      `Send this ${action.channel} to ${action.target_name || (action.channel === "email" ? action.target_email : action.target_phone) || "the customer"}?`
    )) return;
    setApproving(true);
    try {
      // Save any pending edits first
      await api.patch(`/business-coach/actions/${action.id}`, {
        subject: action.subject || "", body: action.body || "",
      }).catch(() => {});
      const { data } = await api.post(`/business-coach/actions/${action.id}/approve`);
      setAction(data);
      if (data.status === "sent") {
        toast.success("Sent");
        onDone?.(data);
      } else {
        toast.error(`Send failed: ${data.reason || "unknown"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally { setApproving(false); }
  };

  const discard = async () => {
    if (!action) return;
    if (!window.confirm("Discard this draft? It will not be sent.")) return;
    try {
      await api.post(`/business-coach/actions/${action.id}/discard`);
      toast.success("Discarded");
      onDone?.();
    } catch (e) {
      toast.error("Failed to discard");
    }
  };

  const applyTemplate = async (tpl) => {
    setAction({ ...action, body: tpl.body || "", subject: tpl.subject || action.subject,
                 template_id: tpl.id });
    // Persist so the log records template_id and body change.
    try {
      await api.patch(`/business-coach/actions/${action.id}`, {
        body: tpl.body || "", subject: tpl.subject || action.subject || "",
      });
      // best-effort mark used
      api.post(`/business-coach/templates/${tpl.id}/mark-used`).catch(() => {});
      toast.success(`Applied template "${tpl.name}"`);
    } catch (e) {
      toast.error("Failed to apply template");
    }
  };

  const useAiDraftInstead = async () => {
    if (!aiFallback) return;
    setAction({ ...action, body: aiFallback.body, subject: aiFallback.subject, template_id: null });
    try {
      await api.patch(`/business-coach/actions/${action.id}`, {
        body: aiFallback.body, subject: aiFallback.subject || "",
      });
    } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" data-testid="coach-action-drawer" onClick={onClose}>
      <div className="w-full sm:w-[560px] bg-white h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 border-b border-zinc-200 bg-white px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono-accent uppercase tracking-widest text-[#FF5A00]">Coach Action</div>
            <div className="font-heading text-xl font-bold">Follow-up draft</div>
            {action?.status && (
              <div className={`inline-block mt-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                action.status === "sent" ? "bg-emerald-100 text-emerald-700" :
                action.status === "failed" ? "bg-rose-100 text-rose-700" :
                action.status === "discarded" ? "bg-zinc-100 text-zinc-600" :
                "bg-orange-100 text-orange-700"
              }`}>{action.status}</div>
            )}
          </div>
          <button onClick={onClose} data-testid="coach-action-close" className="p-2 rounded-md hover:bg-zinc-100">
            <X className="w-5 h-5"/>
          </button>
        </div>

        <div className="p-5 space-y-4">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <Loader2 className="w-4 h-4 animate-spin"/> {drafting ? "Coach is drafting…" : "Loading…"}
            </div>
          )}
          {action && (
            <>
              {suggestions.length > 0 && !isTerminal && (
                <Card className="p-3 border-orange-200 bg-orange-50/40" data-testid="coach-action-suggestions">
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="w-4 h-4 text-[#FF5A00]"/>
                    <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00]">Suggested Template</div>
                  </div>
                  {suggestions.slice(0, 3).map((tpl, i) => (
                    <div key={tpl.id} className="flex items-center justify-between gap-2 py-1"
                         data-testid={`coach-action-suggestion-${i}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">{tpl.name}</div>
                        <div className="text-xs text-zinc-500">
                          {tpl.category ? `${tpl.category} · ` : ""}used {tpl.usage_count || 0}x
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm"
                                onClick={() => setPreviewTpl(tpl)}
                                data-testid={`coach-action-preview-tpl-${i}`}>
                          <Eye className="w-3 h-3 mr-1"/> Preview
                        </Button>
                        <Button size="sm" variant="outline"
                                onClick={() => applyTemplate(tpl)}
                                data-testid={`coach-action-use-tpl-${i}`}>
                          Use template
                        </Button>
                      </div>
                    </div>
                  ))}
                  {action.template_id && aiFallback && (
                    <div className="mt-2">
                      <button
                        onClick={useAiDraftInstead}
                        data-testid="coach-action-use-ai-instead"
                        className="text-xs text-[#FF5A00] underline"
                      >Use AI draft instead</button>
                    </div>
                  )}
                </Card>
              )}
              <Card className="p-3 bg-zinc-50">
                <div className="text-xs text-zinc-500">To</div>
                <div className="font-medium">{action.target_name || "—"}</div>
                {action.channel === "email" && (
                  <Input
                    className="mt-2"
                    placeholder="customer@example.com"
                    value={action.target_email || ""}
                    onChange={(e) => setAction({...action, target_email: e.target.value})}
                    disabled={isTerminal}
                    data-testid="coach-action-target-email"
                  />
                )}
                {action.channel === "sms" && (
                  <Input
                    className="mt-2"
                    placeholder="+15551234567"
                    value={action.target_phone || ""}
                    onChange={(e) => setAction({...action, target_phone: e.target.value})}
                    disabled={isTerminal}
                    data-testid="coach-action-target-phone"
                  />
                )}
              </Card>

              {action.channel === "email" && (
                <SendPreviewPanel
                  preview={preview}
                  loading={previewLoading}
                  hasLinked={!!action.linked_estimate_id}
                  onUnlink={unlinkEstimate}
                  unlinking={unlinking}
                  disabled={isTerminal}
                />
              )}

              <div>
                <div className="flex items-center gap-2 mb-2">
                  {["email", "sms"].map((ch) => {
                    const M = CHANNEL_META[ch];
                    const Icon = M.icon;
                    const active = action.channel === ch;
                    return (
                      <span key={ch}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs ${
                              active ? "bg-orange-50 border border-[#FF5A00] text-[#FF5A00]"
                                     : "text-zinc-400 border border-zinc-200"
                            }`}>
                        <Icon className="w-3 h-3"/> {M.label}
                      </span>
                    );
                  })}
                </div>
                {action.channel === "email" && (
                  <>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500">Subject</Label>
                    <Input
                      className="mt-1 mb-3"
                      value={action.subject || ""}
                      onChange={(e) => setAction({...action, subject: e.target.value})}
                      disabled={isTerminal}
                      data-testid="coach-action-subject"
                    />
                  </>
                )}
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Message</Label>
                <Textarea
                  className="mt-1 min-h-[220px] font-mono text-sm"
                  value={action.body || ""}
                  onChange={(e) => setAction({...action, body: e.target.value})}
                  disabled={isTerminal}
                  data-testid="coach-action-body"
                />
                {action.drafted_by_llm && (
                  <div className="text-[11px] text-zinc-500 mt-1">Drafted by Coach — review before sending.</div>
                )}
              </div>

              {action.status === "sent" && (
                <Card className="p-3 bg-emerald-50 border-emerald-200 flex items-start gap-2 text-sm text-emerald-800"
                      data-testid="coach-action-sent-panel">
                  <CheckCircle2 className="w-4 h-4 mt-0.5"/>
                  <div>
                    <div className="font-medium">Sent</div>
                    <div className="text-xs opacity-80">
                      Approved by {action.approved_by} at {action.approved_at?.replace("T", " ").slice(0, 16)}.
                      Channel: {action.channel}.
                    </div>
                  </div>
                </Card>
              )}
              {action.status === "failed" && (
                <Card className="p-3 bg-rose-50 border-rose-200 text-sm text-rose-800">
                  Send failed: {action.reason || "unknown"}.
                </Card>
              )}

              {!isTerminal && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-200">
                  <Button variant="outline" onClick={save} disabled={saving}
                          data-testid="coach-action-save-btn">
                    {saving ? "Saving…" : "Save draft"}
                  </Button>
                  <Button variant="outline" onClick={discard} className="text-rose-600"
                          data-testid="coach-action-discard-btn">
                    <Trash2 className="w-4 h-4 mr-1"/> Discard
                  </Button>
                  <div className="flex-1"/>
                  <Button
                    onClick={approveSend}
                    disabled={approving || saving}
                    className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
                    data-testid="coach-action-approve-btn"
                  >
                    {approving ? "Sending…" : <><Send className="w-4 h-4 mr-1"/> Approve &amp; send</>}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {previewTpl && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => setPreviewTpl(null)}>
          <div className="bg-white rounded-lg p-5 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00]">Template preview</div>
            <div className="font-heading text-xl font-bold mb-2">{previewTpl.name}</div>
            {previewTpl.subject && (
              <div className="text-sm mb-2"><b>Subject:</b> {previewTpl.subject}</div>
            )}
            <pre className="whitespace-pre-wrap text-sm bg-zinc-50 p-3 rounded font-mono">{previewTpl.body}</pre>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setPreviewTpl(null)}>Close</Button>
              <Button onClick={() => { applyTemplate(previewTpl); setPreviewTpl(null); }}
                      className="bg-[#FF5A00] hover:bg-[#E04F00] text-white">
                Use this template
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function money(n) {
  const v = Number(n || 0);
  return v.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function SendPreviewPanel({ preview, loading, hasLinked, onUnlink, unlinking, disabled }) {
  if (loading) {
    return (
      <Card className="p-3 border-zinc-200" data-testid="coach-send-preview-loading">
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <Loader2 className="w-3 h-3 animate-spin"/> Resolving send preview…
        </div>
      </Card>
    );
  }
  if (!preview) return null;
  const est = preview.linked_estimate;
  const estError = preview.linked_estimate_error;
  const pdf = preview.pdf_attachment;
  return (
    <Card className="p-3 border-zinc-200" data-testid="coach-send-preview">
      <div className="text-[10px] font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-2">Send preview</div>

      {hasLinked && est && (
        <div className="border border-zinc-200 rounded-md p-3 mb-3 bg-white" data-testid="coach-preview-estimate">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <div className="text-xs text-zinc-500">Attached estimate</div>
              <div className="font-heading text-lg font-bold">
                {est.number ? `#${est.number}` : "Estimate"}
                <span className={`ml-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  est.status === "accepted" ? "bg-emerald-100 text-emerald-700"
                  : est.status === "declined" ? "bg-rose-100 text-rose-700"
                  : "bg-orange-100 text-orange-700"
                }`} data-testid="coach-preview-estimate-status">
                  {est.status}
                </span>
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">
                {est.is_view_only
                  ? 'The email will link to "View Accepted Estimate" — no approve/decline buttons.'
                  : 'The email will include a secure "View Estimate" button so the customer can approve or decline.'}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xl font-bold text-zinc-900" data-testid="coach-preview-estimate-total">{money(est.total)}</div>
              {est.expires_at && !est.is_view_only && (
                <div className="text-[10px] text-zinc-500">Expires {String(est.expires_at).slice(0,10)}</div>
              )}
            </div>
          </div>
          <div className="text-xs text-zinc-600 grid grid-cols-2 gap-1 mb-2">
            <div>Subtotal: <b>{money(est.subtotal)}</b></div>
            {est.discount_amount ? <div>Discount: <b>-{money(est.discount_amount)}</b></div> : <div/>}
            {est.tax_total ? <div>Tax: <b>{money(est.tax_total)}</b></div> : <div/>}
            {est.deposit_amount ? <div>Deposit: <b>{money(est.deposit_amount)}</b></div> : <div/>}
          </div>
          {est.line_items?.length > 0 && (
            <div className="text-xs text-zinc-500 mb-2">
              {est.line_items.length} line item{est.line_items.length === 1 ? "" : "s"} · first: <span className="text-zinc-700">{est.line_items[0].description}</span>
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <a href={est.public_url} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 text-xs text-[#FF5A00] hover:underline"
               data-testid="coach-preview-estimate-view">
              <ExternalLink className="w-3 h-3"/> Open public link
            </a>
            {!disabled && (
              <button
                onClick={onUnlink}
                disabled={unlinking}
                className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-rose-600 disabled:opacity-50"
                data-testid="coach-preview-unlink"
              >
                <Link2Off className="w-3 h-3"/> {unlinking ? "Removing…" : "Remove estimate from this email"}
              </button>
            )}
          </div>
        </div>
      )}

      {hasLinked && !est && estError && (
        <div className="border border-rose-200 bg-rose-50 rounded-md p-3 mb-3 text-xs text-rose-800"
             data-testid="coach-preview-estimate-error">
          <div className="flex items-center gap-1 font-semibold mb-1">
            <AlertTriangle className="w-3 h-3"/> Estimate cannot be attached
          </div>
          <div>{estError}</div>
          {!disabled && (
            <button onClick={onUnlink} disabled={unlinking}
                    className="mt-2 inline-flex items-center gap-1 text-rose-700 hover:underline disabled:opacity-50"
                    data-testid="coach-preview-unlink-error">
              <Link2Off className="w-3 h-3"/> {unlinking ? "Removing…" : "Remove estimate & send plain follow-up"}
            </button>
          )}
        </div>
      )}

      {!hasLinked && (
        <div className="text-xs text-zinc-500 mb-3" data-testid="coach-preview-no-estimate">
          No estimate attached. The email will send with the tenant signature only.
        </div>
      )}

      <div className="border-t border-zinc-100 pt-2 mt-1">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 flex items-center gap-1">
          <FileText className="w-3 h-3"/> Signature{preview.signature_is_fallback ? " (auto-generated from your business details)" : ""}
        </div>
        {preview.signature_html
          ? <div className="text-xs text-zinc-700 bg-zinc-50 rounded p-2 border border-zinc-200"
                 data-testid="coach-preview-signature"
                 dangerouslySetInnerHTML={{ __html: preview.signature_html }}/>
          : <div className="text-xs text-zinc-400 italic">No signature configured and no safe business details available yet.</div>}
        {(preview.signature_warnings || []).length > 0 && (
          <div className="mt-2 text-[11px] bg-amber-50 border border-amber-200 rounded p-2 text-amber-800"
               data-testid="coach-preview-signature-warnings">
            <div className="flex items-center gap-1 font-semibold mb-1">
              <AlertTriangle className="w-3 h-3"/> Please review your business details
            </div>
            <ul className="list-disc ml-4 space-y-0.5">
              {preview.signature_warnings.map((w, i) => (<li key={i}>{w}</li>))}
            </ul>
          </div>
        )}
      </div>

      <div className="mt-2 flex items-center gap-1 text-[11px] text-zinc-500"
           data-testid="coach-preview-pdf-status">
        <Paperclip className="w-3 h-3"/>
        {pdf?.supported
          ? "PDF attachment will be included."
          : `PDF attachment not sent — ${pdf?.reason || "unsupported"}`}
      </div>
    </Card>
  );
}
